import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_URL } from "../config";
import "./Videoteca.css";

function timeAgo(isoDate) {
  const diff = Math.floor((Date.now() - new Date(isoDate + "Z")) / 1000);
  if (diff < 60) return "ahora mismo";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function formatDuration(d) {
  return d ? `${d}s` : "";
}

function VideoCard({ item, onPlay, onDownload, onDelete }) {
  const label = item.id;
  const author = item.author;
  const ago = timeAgo(item.created_at);
  const motor = item.motor === "seedance2" ? "Seedance" : "Kling 3";

  return (
    <div className="vt-card">
      <div className="vt-thumb" onClick={() => onPlay(item)}>
        <video
          src={item.video_path}
          muted
          preload="metadata"
          playsInline
        />
        <div className="vt-play-overlay">▶</div>
        <div className="vt-badge-motor">{motor}</div>
        {item.duration && <div className="vt-badge-duration">{formatDuration(item.duration)}</div>}
      </div>
      <div className="vt-meta">
        <div className="vt-meta-left">
          <span className="vt-id">{label}</span>
          <span className="vt-detail">{author} · {ago}</span>
        </div>
        <div className="vt-meta-actions">
          <button className="vt-icon-btn vt-icon-play" onClick={(e) => { e.stopPropagation(); onPlay(item); }} title="Reproducir">▶</button>
          <button className="vt-icon-btn vt-icon-download" onClick={(e) => { e.stopPropagation(); onDownload(item); }} title="Descargar">↓</button>
          <button className="vt-icon-btn vt-icon-delete" onClick={(e) => { e.stopPropagation(); onDelete(item); }} title="Eliminar">✕</button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ item, onCancel, onConfirm }) {
  if (!item) return null;
  return (
    <div className="vt-modal-overlay" onClick={onCancel}>
      <div className="vt-modal-box" onClick={e => e.stopPropagation()}>
        <h3>¿Eliminar este vídeo?</h3>
        <p>Esta acción no se puede deshacer. Se eliminará el vídeo y sus metadatos de la videoteca.</p>
        <div className="vt-modal-id">{item.id}</div>
        <div className="vt-modal-actions">
          <button className="vt-modal-cancel" onClick={onCancel}>Cancelar</button>
          <button className="vt-modal-delete" onClick={onConfirm}>Eliminar</button>
        </div>
      </div>
    </div>
  );
}

function PlayerModal({ item, onClose }) {
  if (!item) return null;
  const videoRef = useRef(null);

  return (
    <div className="vt-modal-overlay" onClick={onClose}>
      <div className="vt-player-box" onClick={e => e.stopPropagation()}>
        <button className="vt-player-close" onClick={onClose}>✕</button>
        <video
          ref={videoRef}
          src={item.video_path}
          controls
          autoPlay
          playsInline
          className="vt-player-video"
        />
        <div className="vt-player-info">
          <span className="vt-id">{item.id}</span>
          <span className="vt-detail">{item.author} · {item.motor} · {item.ratio} · {formatDuration(item.duration)}</span>
          {item.characters && <span className="vt-detail">Personajes: {item.characters}</span>}
          <p className="vt-player-prompt">{item.prompt}</p>
        </div>
      </div>
    </div>
  );
}

export default function Videoteca({ token }) {
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [authorFilter, setAuthorFilter] = useState("");
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [playTarget, setPlayTarget]     = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 24 });
      if (authorFilter) params.set("author", authorFilter);
      const res = await fetch(`${API_URL}/videos?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items);
        setTotal(data.total);
        setPage(data.page);
        setPages(data.pages);
      }
    } finally {
      setLoading(false);
    }
  }, [authorFilter, token]);

  useEffect(() => { load(1); }, [load]);

  const filtered = search.trim()
    ? items.filter(i => i.id.toLowerCase().includes(search.toLowerCase()))
    : items;

  const handleDownload = (item) => {
    const a = document.createElement("a");
    a.href = item.video_path;
    a.download = `${item.id.replace("#", "")}.mp4`;
    a.click();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`${API_URL}/videos/${encodeURIComponent(deleteTarget.id)}`, {
        method: "DELETE",
        headers,
      });
      setDeleteTarget(null);
      load(page);
    } catch (e) {
      console.error("Error al eliminar:", e);
    }
  };

  return (
    <div className="vt-root">
      <div className="vt-main">
        {/* Toolbar */}
        <div className="vt-toolbar">
          <h2 className="vt-title">Videoteca <span className="vt-count">{total} vídeos</span></h2>
          <div className="vt-filters">
            <input
              className="vt-search"
              placeholder="Buscar por ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              className="vt-search"
              placeholder="Filtrar por autor..."
              value={authorFilter}
              onChange={(e) => { setAuthorFilter(e.target.value); }}
              onBlur={() => load(1)}
              onKeyDown={(e) => { if (e.key === "Enter") load(1); }}
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="vt-empty">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="vt-empty">No hay vídeos generados todavía.</div>
        ) : (
          <div className="vt-grid">
            {filtered.map((item) => (
              <VideoCard
                key={item.id}
                item={item}
                onPlay={setPlayTarget}
                onDownload={handleDownload}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}

        {/* Paginación */}
        {pages > 1 && (
          <div className="vt-pagination">
            <button disabled={page <= 1} onClick={() => load(page - 1)}>‹</button>
            <span>{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => load(page + 1)}>›</button>
          </div>
        )}
      </div>

      {/* Modals */}
      <DeleteModal
        item={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
      <PlayerModal
        item={playTarget}
        onClose={() => setPlayTarget(null)}
      />
    </div>
  );
}
